/**
 * Snipe-IT reconcile page at /it/netmon/snipeit.
 *
 * Three sections per spec § Task 8:
 *   1. Filter bar (site selector, optional date range stub)
 *   2. Unmatched devices table with Add-to-Snipe-IT and Mark-Ignored buttons
 *   3. Conflicts table with Resolve button (modal)
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Slash, GitMerge } from 'lucide-react';

import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import {
  hasNetmonAdmin,
  useNetmonSites,
  useSnipeItBulkByOui,
  useSnipeItConflicts,
  useSnipeItCreate,
  useSnipeItMatchCandidates,
  useSnipeItPickers,
  useSnipeItResolveConflict,
  useSnipeItUnmatched,
  type SnipeItUnmatchedRow,
} from '@/lib/useNetmon';

type CreateModalProps = {
  row: SnipeItUnmatchedRow;
  onClose: () => void;
  onCreated: () => void;
};

function CreateModal({ row, onClose, onCreated }: CreateModalProps) {
  const create = useSnipeItCreate();
  const pickers = useSnipeItPickers();
  const [name, setName] = useState(
    row.hostname && row.hostname.length > 0
      ? row.hostname
      : `${row.oui_vendor ?? 'Unknown'} @ ${row.site_id}`,
  );
  const [modelId, setModelId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Default status to "Ready to Deploy" if it exists (most common pick for
  // bringing a newly-tracked device under management). Runs once on pickers
  // load.
  const statusesData = pickers.data?.data.statuses;
  useEffect(() => {
    if (statusId || !statusesData?.length) return;
    const ready = statusesData.find((s) =>
      s.name.toLowerCase().includes('ready'),
    );
    if (ready) setStatusId(String(ready.id));
  }, [statusesData, statusId]);

  const submit = () => {
    setErr(null);
    if (!modelId || !statusId) {
      setErr('Model and Status are required (Snipe-IT enforces both).');
      return;
    }
    create.mutate(
      {
        mac: row.mac,
        site_id: row.site_id,
        ip: row.ip ?? undefined,
        hostname: row.hostname ?? undefined,
        oui_vendor: row.oui_vendor ?? undefined,
        name,
        model_id: Number(modelId),
        status_id: Number(statusId),
        // location_id intentionally omitted — backend auto-resolves from
        // netmon.sites.site_name -> Snipe-IT location name match.
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (e) => {
          setErr(e.message);
        },
      },
    );
  };

  return (
    <Modal title="Add to Snipe-IT" onClose={onClose}>
      <dl className="grid grid-cols-2 gap-1 text-xs mb-3 text-amp-ink dark:text-amp-dark-ink">
        <dt className="text-amp-ink-muted dark:text-amp-dark-ink-muted">MAC</dt>
        <dd className="font-mono">{row.mac}</dd>
        <dt className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Site</dt>
        <dd>{row.site_id}</dd>
        <dt className="text-amp-ink-muted dark:text-amp-dark-ink-muted">IP</dt>
        <dd className="font-mono">{row.ip ?? '--'}</dd>
        <dt className="text-amp-ink-muted dark:text-amp-dark-ink-muted">Vendor</dt>
        <dd>{row.oui_vendor ?? '--'}</dd>
      </dl>
      <Field label="Asset name" value={name} onChange={setName} />
      <SelectField
        label="Model"
        value={modelId}
        onChange={setModelId}
        loading={pickers.isLoading}
        options={(pickers.data?.data.models ?? []).map((m) => ({
          value: String(m.id),
          label: m.category_name ? `${m.category_name} - ${m.name}` : m.name,
        }))}
        placeholder="Pick a Snipe-IT model"
      />
      <SelectField
        label="Status"
        value={statusId}
        onChange={setStatusId}
        loading={pickers.isLoading}
        options={(pickers.data?.data.statuses ?? []).map((s) => ({
          value: String(s.id),
          label: s.name,
        }))}
        placeholder="Pick a status"
      />
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
        Location is auto-resolved from the site name. HQ / Warehouse have
        multiple sub-locations; admin can move the asset later if needed.
      </p>
      {err && (
        <div className="text-xs text-red-700 dark:text-red-300 mb-2">{err}</div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="text-xs border border-amp-line text-amp-ink rounded px-3 py-1.5 dark:border-amp-dark-line dark:text-amp-dark-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={create.isPending}
          className="text-xs border border-amp-olive bg-amp-olive text-white rounded px-3 py-1.5 disabled:opacity-60 dark:bg-amp-olive-dark"
        >
          {create.isPending ? 'Creating...' : 'Create asset'}
        </button>
      </div>
    </Modal>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  loading,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  loading?: boolean;
}) {
  return (
    <label className="block mb-2">
      <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      >
        <option value="">{loading ? 'Loading...' : placeholder ?? 'Select...'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block mb-2">
      <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      />
    </label>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-amp-white dark:bg-amp-dark-surface border border-amp-line dark:border-amp-dark-line rounded-lg w-full max-w-lg p-4">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-medium text-amp-ink dark:text-amp-dark-ink">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

type ResolveModalProps = {
  mac: string;
  siteId: string;
  onClose: () => void;
  onResolved: () => void;
};

function ResolveModal({ mac, siteId, onClose, onResolved }: ResolveModalProps) {
  const candidates = useSnipeItMatchCandidates(mac);
  const resolve = useSnipeItResolveConflict();
  const [chosen, setChosen] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const cs = candidates.data?.data.candidates ?? [];
  const submit = () => {
    if (chosen == null) {
      setErr('Pick one candidate.');
      return;
    }
    setErr(null);
    const others = cs.map((c) => c.asset_id).filter((id) => id !== chosen);
    resolve.mutate(
      { mac, site_id: siteId, chosen_snipeit_asset_id: chosen, other_asset_ids: others },
      {
        onSuccess: () => onResolved(),
        onError: (e) => setErr(e.message),
      },
    );
  };

  return (
    <Modal title={`Resolve conflict for ${mac}`} onClose={onClose}>
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
        Discovered at site {siteId}. Pick which Snipe-IT asset this device should map to.
        The others will get a flagging note added.
      </p>
      {candidates.isLoading ? (
        <div className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading candidates...
        </div>
      ) : candidates.error ? (
        <div className="text-xs text-red-700 dark:text-red-300 mb-2">
          {candidates.error.message}
        </div>
      ) : cs.length === 0 ? (
        <div className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No Snipe-IT candidates returned.</div>
      ) : (
        <ul className="space-y-2 mb-3">
          {cs.map((c) => (
            <li key={c.asset_id}>
              <label className="flex items-start gap-2 border border-amp-line dark:border-amp-dark-line rounded p-2 cursor-pointer">
                <input
                  type="radio"
                  name="candidate"
                  checked={chosen === c.asset_id}
                  onChange={() => setChosen(c.asset_id)}
                  className="mt-1"
                />
                <div className="text-xs text-amp-ink dark:text-amp-dark-ink">
                  <div className="font-medium">
                    {c.name ?? c.asset_tag ?? `Asset ${c.asset_id}`}
                  </div>
                  <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    id={c.asset_id} model={c.model_name ?? '--'} serial={c.serial ?? '--'} status={c.status ?? '--'}
                  </div>
                  <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    rank: {c.rank}
                  </div>
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}
      {err && <div className="text-xs text-red-700 dark:text-red-300 mb-2">{err}</div>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="text-xs border border-amp-line text-amp-ink rounded px-3 py-1.5 dark:border-amp-dark-line dark:text-amp-dark-ink"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={resolve.isPending}
          className="text-xs border border-amp-olive bg-amp-olive text-white rounded px-3 py-1.5 disabled:opacity-60 dark:bg-amp-olive-dark"
        >
          {resolve.isPending ? 'Resolving...' : 'Resolve'}
        </button>
      </div>
    </Modal>
  );
}

type BulkTarget = {
  oui_prefix: string;       // 6 hex chars, e.g. "08:35:71"
  oui_vendor: string | null;
  sample_mac: string;       // the row the user clicked from
  sample_site_id: string;
  sample_hostname: string | null;
};

function BulkByOuiModal({
  target,
  onClose,
  onDone,
}: {
  target: BulkTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const bulk = useSnipeItBulkByOui();
  const pickers = useSnipeItPickers();
  const [nameTemplate, setNameTemplate] = useState(
    `${target.oui_vendor ?? 'Device'} - {site_id}`,
  );
  const [modelId, setModelId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof bulk.mutateAsync>> | null>(null);

  const statusesData = pickers.data?.data.statuses;
  useEffect(() => {
    if (statusId || !statusesData?.length) return;
    const ready = statusesData.find((s) => s.name.toLowerCase().includes('ready'));
    if (ready) setStatusId(String(ready.id));
  }, [statusesData, statusId]);

  const submit = async (dry: boolean) => {
    setErr(null);
    if (!modelId || !statusId) {
      setErr('model_id and status_id required (Snipe-IT enforces both).');
      return;
    }
    try {
      const res = await bulk.mutateAsync({
        oui_prefix: target.oui_prefix,
        name_template: nameTemplate,
        model_id: Number(modelId),
        status_id: Number(statusId),
        dry_run: dry,
      });
      setResult(res);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-amp-line bg-amp-white p-6 shadow-xl dark:border-amp-dark-line dark:bg-amp-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">
          Bulk-create by OUI
        </h2>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          OUI prefix <span className="font-mono">{target.oui_prefix}</span> · vendor{' '}
          <span className="font-medium">{target.oui_vendor ?? 'unknown'}</span> · finds every
          unmatched MAC across the fleet sharing this prefix and creates a Snipe-IT asset for each.
        </p>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="block text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
              Name template (use <span className="font-mono">{'{site_id}'}</span> to substitute)
            </span>
            <input
              type="text"
              value={nameTemplate}
              onChange={(e) => setNameTemplate(e.target.value)}
              className="w-full rounded border border-amp-line bg-amp-white px-2 py-1 text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink"
            />
          </label>
          <div className="flex gap-3">
            <label className="block text-sm flex-1">
              <span className="block text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
                Model
              </span>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                disabled={pickers.isLoading}
                className="w-full rounded border border-amp-line bg-amp-white px-2 py-1 text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink"
              >
                <option value="">{pickers.isLoading ? 'Loading...' : 'Pick a Snipe-IT model'}</option>
                {(pickers.data?.data.models ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.category_name ? `${m.category_name} - ${m.name}` : m.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm flex-1">
              <span className="block text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
                Status
              </span>
              <select
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
                disabled={pickers.isLoading}
                className="w-full rounded border border-amp-line bg-amp-white px-2 py-1 text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink"
              >
                <option value="">{pickers.isLoading ? 'Loading...' : 'Pick a status'}</option>
                {(pickers.data?.data.statuses ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Location auto-resolves per row from each store's Snipe-IT location name.
          </p>
        </div>

        {err ? (
          <div className="mt-3 text-xs text-red-700 dark:text-red-300">{err}</div>
        ) : null}

        {result ? (
          <div className="mt-4 max-h-48 overflow-auto rounded border border-amp-line bg-amp-cream p-3 text-xs dark:border-amp-dark-line dark:bg-amp-dark-bg">
            {result.data.dry_run ? (
              <>
                <div className="mb-2 font-medium">
                  Preview: {result.data.targets.length} unmatched MACs match this OUI
                </div>
                {result.data.preview_names?.map((p) => (
                  <div key={p.site_id}>
                    site <span className="font-mono">{p.site_id}</span> → {p.name}
                  </div>
                ))}
                {result.data.targets.length > 5 ? (
                  <div className="mt-1 text-amp-ink-muted">
                    ... and {result.data.targets.length - 5} more
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="mb-2 font-medium">
                  Created {result.data.created?.length ?? 0} ·
                  Skipped {result.data.skipped?.length ?? 0} ·
                  Errors {result.data.errors?.length ?? 0}
                </div>
                {result.data.errors?.slice(0, 5).map((e) => (
                  <div key={e.mac} className="text-red-700 dark:text-red-300">
                    {e.site_id}/{e.mac}: {e.message}
                  </div>
                ))}
              </>
            )}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            {result && !result.data.dry_run ? 'Close' : 'Cancel'}
          </button>
          {!result || result.data.dry_run ? (
            <>
              <button
                type="button"
                disabled={bulk.isPending}
                onClick={() => submit(true)}
                className="rounded-md border border-amp-line px-3 py-1.5 text-sm hover:bg-amp-cream dark:border-amp-dark-line dark:hover:bg-amp-dark-surface-warm"
              >
                {bulk.isPending ? '...' : 'Preview targets'}
              </button>
              <button
                type="button"
                disabled={bulk.isPending}
                onClick={async () => {
                  await submit(false);
                  // Don't auto-close; let user see the result, then click Close.
                  onDone();
                }}
                className="rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-amp-cream hover:bg-amp-olive-dark disabled:opacity-60 dark:bg-amp-olive-dark dark:text-amp-dark-bg dark:hover:bg-amp-olive"
              >
                {bulk.isPending ? 'Creating...' : 'Create assets for all targets'}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function NetmonSnipeItReconcile() {
  const { user } = useSession();
  const isAdmin = hasNetmonAdmin(user?.groups, user?.superAdmin);
  const sitesQ = useNetmonSites();

  const [siteFilter, setSiteFilter] = useState<string>('all');
  const filters = useMemo(
    () => ({ siteId: siteFilter === 'all' ? undefined : siteFilter, limit: 500 }),
    [siteFilter],
  );

  const unmatched = useSnipeItUnmatched(filters);
  const conflicts = useSnipeItConflicts(siteFilter === 'all' ? undefined : siteFilter);

  const [createTarget, setCreateTarget] = useState<SnipeItUnmatchedRow | null>(null);
  const [resolveTarget, setResolveTarget] = useState<{ mac: string; siteId: string } | null>(null);
  const [bulkTarget, setBulkTarget] = useState<BulkTarget | null>(null);

  // For each OUI prefix in the current rows, how many unmatched rows share it?
  // Used to label the "Bulk N" button per row.
  const ouiCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of unmatched.data?.data.devices ?? []) {
      const oui = r.mac.replace(/:/g, '').slice(0, 6).toUpperCase();
      map.set(oui, (map.get(oui) ?? 0) + 1);
    }
    return map;
  }, [unmatched.data]);

  const unmatchedDevices = unmatched.data?.data.devices ?? [];
  const conflictRows = conflicts.data?.data.conflicts ?? [];

  const filterToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Site
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="ml-2 text-sm border border-amp-line rounded px-2 py-1 bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="all">All sites</option>
          {sitesQ.sites.map((s) => (
            <option key={s.site_id} value={s.site_id}>
              #{s.site_id} {s.site_name}
            </option>
          ))}
        </select>
      </label>
      {!isAdmin && (
        <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Read-only: contact IT for access to the Network monitor tool (it.netmon) to add/resolve assets.
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <Link to="/it/netmon" className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="h-3 w-3" /> Back to sites
        </Link>
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT - Network monitor
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Snipe-IT reconcile
          </h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Devices netmon found that don't yet match a Snipe-IT asset, plus conflicts.
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
        <DataGridShell
          title="Unmatched devices"
          total={unmatched.data?.data.total_count}
          loaded={unmatchedDevices.length}
          countNoun="unmatched devices"
          toolbar={filterToolbar}
        >
          <table className="w-full text-xs">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-4 py-2 text-left">MAC</th>
                <th className="px-4 py-2 text-left">IP</th>
                <th className="px-4 py-2 text-left">Hostname</th>
                <th className="px-4 py-2 text-left">Vendor</th>
                <th className="px-4 py-2 text-left" title="Similar OUI prefix in Snipe-IT. Dominant label = most common asset name with the same OUI.">Similar in Snipe-IT</th>
                <th className="px-4 py-2 text-left">Site</th>
                <th className="px-4 py-2 text-left">Last seen</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {unmatched.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading unmatched devices...</span>
                  </td>
                </tr>
              ) : unmatched.error ? (
                <tr><td colSpan={8} className="px-4 py-4 text-sm text-red-700 dark:text-red-300">{unmatched.error.message}</td></tr>
              ) : unmatchedDevices.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Nothing unmatched. Run a discovery cycle and revisit.</td></tr>
              ) : (
                unmatchedDevices.map((r) => (
                  <tr key={`${r.site_id}-${r.mac}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-amp-ink dark:text-amp-dark-ink">{r.mac}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.ip ?? ''}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.hostname ?? ''}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.oui_vendor ?? ''}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                      {r.oui_siblings ? (
                        <span
                          title={`${r.oui_siblings.count} Snipe-IT asset${r.oui_siblings.count === 1 ? '' : 's'} share this OUI prefix. ${r.oui_siblings.dominant_count} of ${r.oui_siblings.count} labeled "${r.oui_siblings.dominant_label}".`}
                        >
                          <span className="font-medium">{r.oui_siblings.dominant_label}</span>
                          <span className="ml-1 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                            ({r.oui_siblings.dominant_count}/{r.oui_siblings.count})
                          </span>
                        </span>
                      ) : (
                        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{r.site_id}</td>
                    <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{r.last_seen ?? ''}</td>
                    <td className="px-4 py-2 text-right">
                      {isAdmin ? (
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => setCreateTarget(r)}
                            className="text-xs border border-amp-line text-amp-ink rounded px-2 py-1 hover:bg-amp-cream-warm inline-flex items-center gap-1 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                          >
                            <Plus className="h-3 w-3" /> Add
                          </button>
                          {(() => {
                            const oui = r.mac.replace(/:/g, '').slice(0, 6).toUpperCase();
                            const n = ouiCounts.get(oui) ?? 1;
                            const formattedOui = `${oui.slice(0, 2)}:${oui.slice(2, 4)}:${oui.slice(4, 6)}`;
                            return n > 1 ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setBulkTarget({
                                    oui_prefix: formattedOui,
                                    oui_vendor: r.oui_vendor,
                                    sample_mac: r.mac,
                                    sample_site_id: r.site_id,
                                    sample_hostname: r.hostname,
                                  })
                                }
                                title={`${n} unmatched MACs across the fleet share this OUI prefix. Bulk-create assets for all of them in one go.`}
                                className="text-xs border border-amp-olive text-amp-olive rounded px-2 py-1 hover:bg-amp-olive hover:text-amp-cream inline-flex items-center gap-1 dark:border-amp-olive-dark dark:text-amp-olive-dark dark:hover:bg-amp-olive-dark dark:hover:text-amp-dark-bg"
                              >
                                <GitMerge className="h-3 w-3" /> Bulk {n}
                              </button>
                            ) : null;
                          })()}
                          <button
                            type="button"
                            disabled
                            title="Mark Ignored (pending: requires a small backend addition to set reconcile_status='ignored')"
                            className="text-xs border border-amp-line text-amp-ink-muted rounded px-2 py-1 cursor-not-allowed opacity-60 inline-flex items-center gap-1 dark:border-amp-dark-line dark:text-amp-dark-ink-muted"
                          >
                            <Slash className="h-3 w-3" /> Ignore
                          </button>
                        </div>
                      ) : (
                        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>

        <DataGridShell
          title="Conflicts"
          loaded={conflictRows.length}
          countNoun="conflicts"
        >
          <table className="w-full text-xs">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-4 py-2 text-left">MAC</th>
                <th className="px-4 py-2 text-left">Reason</th>
                <th className="px-4 py-2 text-left">Sites</th>
                <th className="px-4 py-2 text-left">Last seen</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {conflicts.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading conflicts...</span>
                  </td>
                </tr>
              ) : conflicts.error ? (
                <tr><td colSpan={5} className="px-4 py-4 text-sm text-red-700 dark:text-red-300">{conflicts.error.message}</td></tr>
              ) : conflictRows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No conflicts.</td></tr>
              ) : (
                conflictRows.map((c, idx) => (
                  <tr key={`${c.mac}-${idx}`}>
                    <td className="px-4 py-2 font-mono text-[11px] text-amp-ink dark:text-amp-dark-ink">{c.mac}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">{c.reason}</td>
                    <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                      {c.site_ids ? c.site_ids.join(', ') : c.site_id ?? '--'}
                    </td>
                    <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{c.last_seen ?? ''}</td>
                    <td className="px-4 py-2 text-right">
                      {isAdmin && c.reason === 'snipeit_ambiguous' && c.site_id ? (
                        <button
                          type="button"
                          onClick={() =>
                            setResolveTarget({ mac: c.mac, siteId: c.site_id as string })
                          }
                          className="text-xs border border-amp-line text-amp-ink rounded px-2 py-1 hover:bg-amp-cream-warm inline-flex items-center gap-1 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                        >
                          <GitMerge className="h-3 w-3" /> Resolve
                        </button>
                      ) : (
                        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>
      </div>

      {createTarget && (
        <CreateModal
          row={createTarget}
          onClose={() => setCreateTarget(null)}
          onCreated={() => {
            setCreateTarget(null);
            void unmatched.refetch();
          }}
        />
      )}
      {resolveTarget && (
        <ResolveModal
          mac={resolveTarget.mac}
          siteId={resolveTarget.siteId}
          onClose={() => setResolveTarget(null)}
          onResolved={() => {
            setResolveTarget(null);
            void conflicts.refetch();
          }}
        />
      )}
      {bulkTarget && (
        <BulkByOuiModal
          target={bulkTarget}
          onClose={() => {
            setBulkTarget(null);
            void unmatched.refetch();
          }}
          onDone={() => {
            void unmatched.refetch();
          }}
        />
      )}
    </div>
  );
}
